import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import checkmarkCirclePath from '@ingka/ssr-icon/paths/checkmark-circle';
import Text from '@ingka/text';
import { useNavigate } from 'react-router';
import { useApp } from '@/context/AppContext';
import { useClassificationName, useEntityName, useProductName } from '@/hooks/useCatalogNames';
import { fmtDate, fmtSize, fmtTime } from '@/utils/formatters';
import styles from './SuccessStep.module.css';

function ReceiptRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className={styles.rowLabel}>{label}</p>
      <p className={styles.rowValue}>{value}</p>
    </div>
  );
}

export default function SuccessStep() {
  const { user, lastUpload, startOver } = useApp();
  const navigate = useNavigate();
  const productLabel = useProductName(lastUpload?.product);
  const entityLabel = useEntityName(lastUpload?.product, lastUpload?.entity);
  const classificationLabel = useClassificationName(lastUpload?.classification);

  if (!lastUpload) return null;

  return (
    <div className={styles.page}>
      {/* ── Success banner ──────────────────────────────────────────────────── */}
      <div className={styles.banner}>
        <div className={styles.bannerBlob} />
        <div className={styles.bannerIcon}>
          <SSRIcon paths={checkmarkCirclePath} />
        </div>
        <Text tagName="h2" textStyle="Heading.XL" className={styles.bannerTitle}>
          File uploaded.
        </Text>
        <Text tagName="p" textStyle="Body.L" className={styles.bannerBody}>
          Your file was received, validated and routed. A copy is in the archive.
        </Text>
      </div>

      {/* ── Receipt ─────────────────────────────────────────────────────────── */}
      <div className={styles.receipt}>
        <div className={styles.receiptHeader}>
          <div>
            <p className={styles.receiptConfirmLabel}>Confirmation number</p>
            <p className={styles.receiptConfirmId}>{lastUpload.id}</p>
          </div>
          <span className={styles.receiptBadge}>
            <SSRIcon paths={checkmarkCirclePath} /> Success
          </span>
        </div>

        <div className={styles.receiptGrid}>
          <ReceiptRow label="File name" value={lastUpload.fileName} />
          <ReceiptRow label="File size" value={fmtSize(lastUpload.sizeBytes)} />
          <ReceiptRow label="Product" value={productLabel} />
          <ReceiptRow label="Entity" value={entityLabel} />
          <ReceiptRow label="Classification" value={classificationLabel} />
          <ReceiptRow label="Uploaded by" value={user?.name || lastUpload.uploadedBy} />
          <ReceiptRow
            label="Uploaded at"
            value={`${fmtDate(lastUpload.uploadedAt)}, ${fmtTime(lastUpload.uploadedAt)}`}
          />
        </div>
      </div>

      {/* ── Actions ─────────────────────────────────────────────────────────── */}
      <div className={styles.actions}>
        <Button
          type="emphasised"
          text="Upload another file"
          onClick={() => {
            startOver();
            navigate('/upload/product');
          }}
        />
        <Button type="secondary" text="Go to archive" onClick={() => navigate('/archive')} />
      </div>
    </div>
  );
}
